import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import Logo, { LogoMark } from '../components/Logo';
import Footer from '../components/Footer';
import { FieldLabel, FieldInput } from '../components/FormField';
import { VENDOR_BASE_PATH, VENDOR_TOKEN_KEY } from '../lib/vendorApi';
import { useTheme } from '../hooks/useTheme';

const VendorLogin = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch(`/api/${VENDOR_BASE_PATH}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        setError('Invalid username or password.');
        return;
      }

      const data = await res.json();
      sessionStorage.setItem(VENDOR_TOKEN_KEY, data.access_token);
      navigate(`/${VENDOR_BASE_PATH}/dashboard`);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--zg-surface)' }}>
      <div className="border-b" style={{ borderColor: 'var(--zg-border)', background: 'var(--zg-card)' }}>
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo markSize={30} wordmarkSize={22} />
            <span className="hidden sm:block h-5 w-px" style={{ background: 'var(--zg-border2)' }} />
            <span className="hidden sm:inline text-sm font-semibold" style={{ color: 'var(--zg-muted)' }}>
              Zeevogo Vendor Admin Portal
            </span>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            title="Toggle theme"
            className="inline-flex items-center justify-center w-[42px] h-[42px] rounded-full border transition-colors"
            style={{ borderColor: 'var(--zg-border)', color: 'var(--zg-ink)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#e08e0b';
              e.currentTarget.style.color = '#e08e0b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--zg-border)';
              e.currentTarget.style.color = 'var(--zg-ink)';
            }}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      <div className="relative overflow-hidden flex-1 flex items-center justify-center px-4 py-16">
        <LogoMark
          size={560}
          extraRing
          opacity={0.07}
          style={{ position: 'absolute', right: -130, top: 20, pointerEvents: 'none' }}
        />
        <LogoMark
          size={300}
          extraRing
          ink="#e08e0b"
          fill="#e08e0b"
          dotFill="#e08e0b"
          opacity={0.06}
          style={{ position: 'absolute', left: -110, bottom: -90, pointerEvents: 'none' }}
        />
        <div
          className="w-full max-w-sm rounded-[24px] border px-[34px] py-10"
          style={{ borderColor: 'var(--zg-border)', background: 'var(--zg-card)', boxShadow: '0 20px 50px rgba(45,42,36,.07)' }}
        >
          <div className="flex flex-col items-center mb-6">
            <div
              className="w-[54px] h-[54px] rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(224,142,11,.12)', color: '#c67e0a' }}
            >
              <Icon name="lock" size={26} />
            </div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--zg-ink)' }}>Vendor Sign In</h1>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <FieldLabel htmlFor="username">Username</FieldLabel>
              <FieldInput
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <FieldInput
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center h-11 rounded-full text-sm font-medium text-white transition-all disabled:opacity-70"
              style={{ background: '#2d2a24' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#3a362e';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2d2a24';
              }}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VendorLogin;
